/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo } from "react";
import { Table, Button, Alert, Select, Empty, Tag, Input } from "antd";
import { ArrowRightOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import useTransfer from "../hooks/useTransfer";
import useParseString from "../hooks/useParseString";
import useUsers from "../hooks/useUsers";
import useCompanies from "../hooks/useCompanies";
import { useNavigate, useLocation, useSearchParams } from "react-router";
import { FolderClock, X, AlertTriangle, RefreshCw, Search } from "lucide-react";
import DebtModal from "../components/transfers/DebtModal";
import PageDocumentation from "../components/PageDocumentation";

function Debts({ defaultType }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { debts, companyDebts, getDebts, getCompanyDebts, clearCache, clearDebts, loading } =
    useTransfer();
  const { formatCurrency, formatUserType } = useParseString();
  const { getUsers, users } = useUsers();
  const { getCompanies, companies } = useCompanies();

  // Títulos por tipo
  const titleByType = {
    USER_SELLER: "Deudas de Vendedores",
    USER_GUIDE: "Deudas de Guías",
    USER_COORDINATOR: "Rendiciones de Coordinadores",
    COMPANY: "Rendiciones de Empresas",
  };

  // Initialize state from URL parameters
  const getInitialParams = () => {
    const urlType = defaultType || searchParams.get("type");
    const urlPage = searchParams.get("page");
    const urlUserId = searchParams.get("user_id");
    const urlCompanyId = searchParams.get("company_id");

    // Auto-load if defaultType is provided OR if there are actual filter params
    const hasFilterParams = urlUserId || urlCompanyId;

    return {
      page: urlPage ? parseInt(urlPage) : 1,
      type: urlType || "USER_SELLER",
      initialLoad: defaultType ? true : hasFilterParams, // Auto-load if defaultType or filters applied
      ...(urlUserId && { user_id: parseInt(urlUserId) }),
      ...(urlCompanyId && { id: parseInt(urlCompanyId) }),
    };
  };

  const [params, setParams] = useState(getInitialParams());
  const [tableKey, setTableKey] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState(
    searchParams.get("user_id") ? parseInt(searchParams.get("user_id")) : null
  );
  const [selectedCompanyId, setSelectedCompanyId] = useState(
    searchParams.get("company_id") ? parseInt(searchParams.get("company_id")) : null
  );
  const [dataSource, setDataSource] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [debtModal, setDebtModal] = useState({
    open: false,
    userId: null,
    type: null,
    extraInfo: null,
    transferId: null
  });
  const [sortOrder, setSortOrder] = useState('descend'); // 'ascend' o 'descend'
  const [searchText, setSearchText] = useState('');

  // Actualizar params cuando cambia defaultType (navegación entre secciones)
  useEffect(() => {
    if (defaultType) {
      clearDebts(); // Limpiar datos anteriores del hook
      setDataSource([]);
      setTotalCount(0);
      setSelectedUserId(null);
      setSelectedCompanyId(null);
      setSearchText(''); // Limpiar búsqueda
      setParams({
        page: 1,
        type: defaultType,
        initialLoad: true,
      });
      setTableKey((prev) => prev + 1);
    }
  }, [defaultType]);

  // Helper function to update URL parameters
  const updateURLParams = (newParams) => {
    const urlParams = new URLSearchParams();

    if (newParams.type) {
      urlParams.set("type", newParams.type);
    }
    if (newParams.page && newParams.page !== 1) {
      urlParams.set("page", newParams.page.toString());
    }
    if (newParams.user_id) {
      urlParams.set("user_id", newParams.user_id.toString());
    }
    if (newParams.id) {
      urlParams.set("company_id", newParams.id.toString());
    }

    setSearchParams(urlParams);
  };

  useEffect(() => {
    if (!params.initialLoad) {
      setDataSource([]);
      setTotalCount(0);
      return;
    }

    const ordering = sortOrder === 'descend' ? '-daysInDebt' : 'daysInDebt';

    if (params.type === "COMPANY") {
      getCompanyDebts(params.id ? { id: params.id, ordering } : { ordering });
    } else {
      getDebts({ ...params, ordering });
    }
  }, [params, sortOrder]);

  useEffect(() => {
    if (params.type === "COMPANY" && companyDebts?.results) {
      setDataSource(companyDebts.results);
      setTotalCount(companyDebts.count || 0);
    } else if (debts?.results) {
      setDataSource(debts.results);
      setTotalCount(debts.count || 0);
    }
  }, [debts, companyDebts, params.type]);

  useEffect(() => {
    if (params.type !== "COMPANY") {
      getUsers();
    } else {
      getCompanies();
    }
  }, [params.type]);

  // Función para normalizar texto (quitar acentos y convertir a minúsculas)
  const normalizeText = (text) => {
    if (!text) return '';
    return text
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  };

  // Función para calcular similitud simple entre dos strings
  const calculateSimilarity = (str1, str2) => {
    const s1 = normalizeText(str1);
    const s2 = normalizeText(str2);

    if (s1.includes(s2) || s2.includes(s1)) return 1;

    // Buscar palabras que coincidan
    const words1 = s1.split(/\s+/);
    const words2 = s2.split(/\s+/);

    let matches = 0;
    for (const word of words2) {
      if (word.length < 2) continue;
      if (words1.some(w => w.includes(word) || word.includes(w))) {
        matches++;
      }
    }

    return matches / Math.max(words2.length, 1);
  };

  // Filtrar datos por búsqueda
  const filteredDataSource = useMemo(() => {
    if (!searchText.trim()) return dataSource;

    const searchTerms = normalizeText(searchText);

    return dataSource.filter(record => {
      // Buscar en nombre/título
      const titleMatch = normalizeText(record.title).includes(searchTerms);

      // Buscar en descripción
      const descMatch = normalizeText(record.description).includes(searchTerms);

      // Buscar en monto (formatear el número para buscar)
      const amountStr = Math.abs(record.totalAmount || 0).toString();
      const amountMatch = amountStr.includes(searchTerms.replace(/[^0-9]/g, ''));

      // Buscar por similitud si no hay coincidencia exacta
      if (titleMatch || descMatch || amountMatch) return true;

      // Búsqueda por similitud en título
      const similarity = calculateSimilarity(record.title, searchText);
      return similarity > 0.5;
    });
  }, [dataSource, searchText]);

  const handleTableChange = (pagination, filters, sorter) => {
    // Manejar ordenamiento
    if (sorter && sorter.field === 'daysInDebt') {
      const newSortOrder = sorter.order || 'descend';
      if (newSortOrder !== sortOrder) {
        setSortOrder(newSortOrder);
        return; // El useEffect se encargará de refrescar los datos
      }
    }

    // Manejar paginación
    if (pagination && pagination.current !== params.page) {
      setParams((prevParams) => {
        const newParams = { ...prevParams, page: pagination.current };
        updateURLParams(newParams);
        return newParams;
      });
    }
  };

  const userTypeOptions = [
    { label: "Coordinadores", value: "USER_COORDINATOR" },
    { label: "Vendedores", value: "USER_SELLER" },
    { label: "Guías", value: "USER_GUIDE" },
    { label: "Empresas", value: "COMPANY" },
  ];

  const userOptions = users.map((user) => ({
    label: `${user.firstName} ${user.lastName}`,
    value: user.id,
  }));

  const companyOptions = companies.results?.map((company) => ({
    label: company.name,
    value: company.id,
  }));

  const handleUserFilterChange = (userId) => {
    setSelectedUserId(userId);
  };

  const handleCompanyFilterChange = (companyId) => {
    setSelectedCompanyId(companyId);
  };

  const handleApplyUserFilter = (confirm) => {
    setDataSource([]);
    setTotalCount(0);

    setParams((prevParams) => {
      const newParams = {
        ...prevParams,
        user_id: selectedUserId || undefined,
        page: 1,
        initialLoad: true,
      };

      // Update URL with new params
      updateURLParams(newParams);

      return newParams;
    });
    setTableKey((prevKey) => prevKey + 1);
    confirm && confirm();
  };

  const handleApplyCompanyFilter = (confirm) => {
    setDataSource([]);
    setTotalCount(0);

    setParams((prevParams) => {
      const newParams = {
        ...prevParams,
        id: selectedCompanyId || undefined,
        page: 1,
        initialLoad: true,
      };

      // Update URL with new params
      updateURLParams(newParams);

      return newParams;
    });
    setTableKey((prevKey) => prevKey + 1);
    confirm && confirm();
  };

  const handleClearUserFilter = (confirm) => {
    setSelectedUserId(null);
    setDataSource([]);
    setTotalCount(0);

    setParams((prevParams) => {
      const newParams = { ...prevParams };
      delete newParams.user_id;

      // Update URL with cleared params
      updateURLParams(newParams);

      return newParams;
    });
    setTableKey((prevKey) => prevKey + 1);
    confirm && confirm();
  };

  const handleClearCompanyFilter = (confirm) => {
    setSelectedCompanyId(null);
    setDataSource([]);
    setTotalCount(0);

    setParams((prevParams) => {
      const newParams = { ...prevParams };
      delete newParams.id;

      // Update URL with cleared params
      updateURLParams(newParams);

      return newParams;
    });
    setTableKey((prevKey) => prevKey + 1);
    confirm && confirm();
  };

  const handleLoadAllDebts = () => {
    setDataSource([]);
    setTotalCount(0);

    setParams((prevParams) => {
      const newParams = {
        ...prevParams,
        initialLoad: true,
      };

      // Update URL to reflect that we're loading all debts
      updateURLParams(newParams);

      return newParams;
    });
    setTableKey((prevKey) => prevKey + 1);
  };

  const dropdownRender = (menu) => (
    <div>
      {menu}
      {selectedUserId && (
        <div style={{ padding: "8px", textAlign: "center" }}>
          <Button
            type="text"
            size="small"
            onClick={handleClearUserFilter}
            icon={<X size={14} />}
          >
            Clear filter
          </Button>
        </div>
      )}
    </div>
  );

  const baseColumns = [
    {
      title: "Nombre",
      dataIndex: "title",
      key: "title",
      fixed: "left",
      width: 150,
    },
    {
      title: "Descripción",
      dataIndex: "description",
      key: "description",
    },
  ];

  const typeColumns = {
    USER_SELLER: [
      {
        title: "Total recaudado",
        dataIndex: "incomeAmount",
        key: "incomeAmount",
        render: (text) => formatCurrency(text),
      },
      {
        title: "Gastos",
        dataIndex: "expensesAmount",
        key: "expensesAmount",
        render: (text) => formatCurrency(text),
      },
      {
        title: "Comisión",
        dataIndex: "commissionAmount",
        key: "commissionAmount",
        render: (text) => formatCurrency(text),
      },
    ],
    USER_GUIDE: [
      {
        title: "Total recaudado",
        dataIndex: "incomeAmount",
        key: "incomeAmount",
        render: (text) => formatCurrency(text),
      },
      {
        title: "Gastos",
        dataIndex: "expensesAmount",
        key: "expensesAmount",
        render: (text) => formatCurrency(text),
      },
    ],
    USER_COORDINATOR: [],
    COMPANY: [],
  };

  const daysInDebtColumn = {
    title: "Días en deuda",
    dataIndex: "daysInDebt",
    key: "daysInDebt",
    width: 130,
    sorter: true, // Habilita el ícono de ordenamiento
    sortOrder: sortOrder, // Orden controlado desde el estado
    showSorterTooltip: { title: "Ordenar por días en deuda" },
    render: (days) => {
      if (days === undefined || days === null) return "-";
      const color = days > 30 ? "text-red-500" : days > 15 ? "text-yellow-500" : "text-green-600";
      return <span className={`font-medium ${color}`}>{days} días</span>;
    },
  };


  const actionColumn = {
    title: "",
    key: "action",
    fixed: "right",
    width: 150,
    render: (text, record) => (
      <Button type="link" onClick={() => handleRendirDinero(record)}>
        Rendir dinero
      </Button>
    ),
  };

  // Columna de estado de deuda - indica claramente la dirección del dinero
  const debtStatusColumn = {
    title: "Estado",
    key: "debtStatus",
    width: 130,
    render: (_, record) => {
      // Usar totalAmount para determinar la dirección (tiene el signo correcto)
      // pendingAmount siempre es positivo (usa abs() en backend)
      const direction = record.totalAmount;
      const pendingAmt = record.pendingAmount ?? Math.abs(record.totalAmount);

      // Si no hay monto pendiente, está saldado
      if (pendingAmt === 0) {
        return (
          <Tag color="green">SALDADO</Tag>
        );
      }

      if (direction > 0) {
        // Ellos tienen que rendir dinero
        return (
          <Tag color="orange" className="flex items-center gap-1 w-fit">
            <ArrowRightOutlined /> DEBE RENDIR
          </Tag>
        );
      } else {
        // Pendiente a rendirle a ellos
        return (
          <Tag color="blue" className="flex items-center gap-1 w-fit">
            <ArrowLeftOutlined /> A RENDIRLE
          </Tag>
        );
      }
    },
  };

  const totalColumn = {
    title: "Monto",
    dataIndex: "totalAmount",
    key: "totalAmount",
    render: (text, record) => {
      // Usar totalAmount para la dirección (tiene el signo)
      // pendingAmount para el monto a mostrar (siempre positivo)
      const direction = record.totalAmount;
      const displayAmount = record.pendingAmount ?? Math.abs(text);
      const mustRender = direction > 0;
      const pendingToRender = direction < 0;

      return (
        <div className="flex flex-col">
          <span className={`font-semibold ${mustRender ? "text-orange-600" : pendingToRender ? "text-blue-600" : "text-green-600"}`}>
            {formatCurrency(displayAmount)}
          </span>
          <span className="text-xs text-gray-400">
            {displayAmount === 0 ? "" : mustRender ? "a rendir" : "a rendirle"}
          </span>
        </div>
      );
    },
  };

  const getColumnsByType = (type) => {
    if (type === "COMPANY") {
      return [
        {
          title: "Nombre",
          dataIndex: "title",
          key: "title",
          fixed: "left",
          width: 150,
          filterDropdown: ({ confirm }) => (
            <div className="p-3" style={{ width: 250 }}>
              <div className="mb-4">
                <span className="mb-2 block font-medium">
                  Filtro por empresa:
                </span>
                <Select
                  placeholder="Seleccionar empresa"
                  onChange={handleCompanyFilterChange}
                  value={selectedCompanyId}
                  allowClear
                  className="w-full mb-2"
                  options={companyOptions}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  optionFilterProp="label"
                />
                <Button
                  size="small"
                  onClick={() => handleClearCompanyFilter(confirm)}
                  className="mr-1"
                >
                  Limpiar
                </Button>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => handleApplyCompanyFilter(confirm)}
                  className="float-right"
                >
                  Aplicar
                </Button>
              </div>
            </div>
          ),
          filtered: !!selectedCompanyId,
        },
        {
          title: "Descripción",
          dataIndex: "description",
          key: "description",
        },
        debtStatusColumn,
        totalColumn,
        daysInDebtColumn,
        actionColumn,
      ];
    }

    const extraColumns = typeColumns[type] || typeColumns.USER_SELLER;

    const typeColumn =
      type === "USER_SELLER" && params.type === "USER_SELLER"
        ? [
            {
              title: "Tipo",
              dataIndex: "type",
              key: "type",
              render: (text) => formatUserType(text),
            },
          ]
        : [];


    const nameColumnWithFilter = {
      title: "Nombre",
      dataIndex: "title",
      key: "title",
      fixed: "left",
      width: 150,
      filterDropdown: ({ confirm }) => (
        <div className="p-3" style={{ width: 250 }}>
          <div className="mb-4">
            <span className="mb-2 block font-medium">Filtro por usuario:</span>
            <Select
              placeholder="Seleccionar usuario"
              onChange={handleUserFilterChange}
              value={selectedUserId}
              allowClear
              className="w-full mb-2"
              options={userOptions}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              optionFilterProp="label"
            />
            <Button
              size="small"
              onClick={() => handleClearUserFilter(confirm)}
              className="mr-1"
            >
              Limpiar
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={() => handleApplyUserFilter(confirm)}
              className="float-right"
            >
              Aplicar
            </Button>
          </div>
        </div>
      ),
      filtered: !!selectedUserId,
    };

    return [
      nameColumnWithFilter,
      {
        title: "Descripción",
        dataIndex: "description",
        key: "description",
      },
      ...typeColumn,
      ...extraColumns,
      debtStatusColumn,
      totalColumn,
      daysInDebtColumn,
      actionColumn,
    ];
  };

  const columns = getColumnsByType(params.type);

  const handleRendirDinero = (record) => {
    if (record.transferType === "DEBT") {
      setDebtModal({
        open: true,
        userId: record.id,
        type: record.type,
        extraInfo: record.extraInfo,
        transferId: null
      });
    } else {
      setDebtModal({
        open: true,
        userId: null,
        type: null,
        extraInfo: null,
        transferId: record.id
      });
    }
  };

  const handleTypeChange = (value) => {
    setDataSource([]);
    setTotalCount(0);
    setSearchText(''); // Limpiar búsqueda al cambiar tipo

    setParams((prevParams) => {
      const newParams = {
        ...prevParams,
        type: value,
        page: 1,
        initialLoad: false,
      };

      if (value === "COMPANY") {
        delete newParams.user_id;
        setSelectedUserId(null);
      } else {
        delete newParams.id;
        setSelectedCompanyId(null);
      }

      // Update URL with new params
      updateURLParams(newParams);

      return newParams;
    });

    setTableKey((prevKey) => prevKey + 1);
  };

  const handleClearCache = async () => {
    const result = await clearCache();
    if (result) {
      // Reload the table data after clearing cache
      if (params.initialLoad) {
        if (params.type === "COMPANY") {
          getCompanyDebts(params.id ? { id: params.id } : undefined);
        } else {
          getDebts(params);
        }
      }
    }
  };

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-10">
      <Empty description="Para empezar la búsqueda selecciona una persona" />
      <div className="mt-4">
        <Button type="primary" onClick={handleLoadAllDebts} className="mt-2">
          Cargar todas las deudas
        </Button>
        <Alert
          message={
            <div className="flex items-center mt-2">
              <AlertTriangle size={16} className="mr-2" />
              <span>Esta operación puede tardar unos momentos</span>
            </div>
          }
          type="warning"
          className="mt-2"
          showIcon={false}
        />
      </div>
    </div>
  );

  return (
    <div className="">
      <div className="w-full mb-4 flex justify-between">
        <span className="text-lg font-semibold ">
          {defaultType ? titleByType[defaultType] : "Listado de deudas"}
        </span>

        <div className="flex gap-2 items-center">
          <PageDocumentation docKey="debts" />
          <Button
            icon={<RefreshCw size={16} />}
            onClick={handleClearCache}
            loading={loading.post}
          >
            Actualizar
          </Button>
          <Button
            type="primary"
            icon={<FolderClock size={16} />}
            onClick={() => {
              navigate("/transfers/history");
            }}
          >
            Historial
          </Button>
          <Button
            type="default"
            onClick={() => {
              navigate("/caja");
            }}
          >
            Caja
          </Button>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap gap-4 items-end">
        {!defaultType && (
          <div>
            <span className="text-md font-semibold block mb-1">Tipo</span>
            <Select
              popupMatchSelectWidth={false}
              value={params.type}
              onChange={handleTypeChange}
              options={userTypeOptions}
              placeholder="Tipos de usuario"
              style={{ minWidth: 150 }}
            />
          </div>
        )}

        <div className="flex-1" style={{ minWidth: 250, maxWidth: 400 }}>
          <span className="text-md font-semibold block mb-1">Buscar</span>
          <Input
            placeholder="Buscar por nombre, descripción o monto..."
            prefix={<Search size={16} className="text-gray-400" />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
        </div>

        {searchText && filteredDataSource.length !== dataSource.length && (
          <div className="text-sm text-gray-500 self-end pb-2">
            {filteredDataSource.length} de {dataSource.length} resultados
          </div>
        )}
      </div>

      <Table
        key={tableKey}
        loading={loading.get}
        columns={columns}
        dataSource={params.initialLoad ? filteredDataSource : []}
        rowKey="id"
        scroll={{ x: 1200 }}
        onChange={handleTableChange}
        pagination={{
          current: params.page,
          pageSize: filteredDataSource?.length || 10,
          total: searchText ? filteredDataSource.length : totalCount,
        }}
        locale={{
          emptyText: !params.initialLoad ? (
            <EmptyState />
          ) : (
            "No hay datos disponibles"
          ),
        }}
        onRow={(record) => ({
          onClick: (e) => {
            if (e.target.closest('.ant-btn, .ant-select, a')) return;
            handleRendirDinero(record);
          },
          style: { cursor: 'pointer' }
        })}
      />

      <DebtModal
        open={debtModal.open}
        onClose={() => setDebtModal({ open: false, userId: null, type: null, extraInfo: null, transferId: null })}
        userId={debtModal.userId}
        type={debtModal.type}
        extraInfo={debtModal.extraInfo}
        transferId={debtModal.transferId}
        onSuccess={() => {
          setDebtModal({ open: false, userId: null, type: null, extraInfo: null, transferId: null });
          if (params.type === "COMPANY") {
            getCompanyDebts(params.id ? { id: params.id } : undefined);
          } else {
            getDebts(params);
          }
        }}
      />
    </div>
  );
}

export default Debts;
